import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import { Drawer, IconButton } from "@mui/material";
import { Typography } from "@avenue-8/sidekick-ui";
import {
  SIDEBAR_WIDTH_COLLAPSED_PX,
  SIDEBAR_WIDTH_EXPANDED_PX,
} from "@shared/layouts/sidebar-dimensions";

export const ChatSidebarDrawer = styled(Drawer)`
  flex-shrink: 0;

  & .MuiDrawer-paper {
    width: ${SIDEBAR_WIDTH_COLLAPSED_PX}px;
    transition: width 0.3s ease-in-out;
    background-color: ${(p) => p.theme.colors.background2};
    box-sizing: border-box;
    /* Keep sidebar above page content during open/close animation */
    z-index: 99;
    border: none;
  }

  &.sidebar-expanded .MuiDrawer-paper {
    width: ${SIDEBAR_WIDTH_EXPANDED_PX}px;
  }

  ${(p) => p.theme.media.laptopDown} {
    width: 100%;

    & .MuiDrawer-paper {
      width: 100%;
      /* Mobile temporary drawer should sit above everything */
      z-index: 1300;
    }
  }
`;

const contentTransitionStyles = `
  &.sidebar-content-enter {
    opacity: 0;
  }
  &.sidebar-content-enter-active {
    opacity: 1;
    transition: opacity 130ms ease-in;
  }
  &.sidebar-content-exit {
    opacity: 1;
  }
  &.sidebar-content-exit-active {
    opacity: 0;
    transition: opacity 100ms ease-in;
  }
`;

export const DrawerContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 4rem auto 1fr auto;
  height: 100%;
  @supports (height: 100dvh) {
    height: 100dvh;
    max-height: 100dvh;
  }
  overflow: hidden;
  ${contentTransitionStyles}

  ${(p) => p.theme.media.laptopDown} {
    grid-template-rows: 4.5rem auto 1fr auto;
  }
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.75rem;
  background-color: ${(p) => p.theme.colors.background2};
  border-bottom: 1px solid ${(p) => p.theme.colors.border2};
`;

export const SidebarNav = styled.nav`
  padding: 1rem 0;
`;

export const SidebarNavItem = styled.div<{ $active?: boolean }>`
  width: 100%;
  color: ${(p) => p.theme.text.colors.brand};
  background-color: ${(p) =>
    p.$active ? p.theme.colors.background5 : "transparent"};
  font-size: ${(p) => p.theme.text.fontSizes.l};
  font-weight: 700;

  &:hover {
    background-color: ${(p) => p.theme.colors.background5};
  }
`;

export const SidebarNavLink = styled(Link)`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  text-decoration: none;
`;

export const SidebarNavComingSoonItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  cursor: default;
  opacity: 0.6;
`;

export const ComingSoonLabel = styled(Typography)`
  margin-left: auto;
  font-size: ${(p) => p.theme.text.fontSizes.xs};
`;

export const SidebarNavComingSoonItemContent = styled.span`
  font-size: ${(p) => p.theme.text.fontSizes.l};
  font-weight: 700;
  color: ${(p) => p.theme.text.colors.brand};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const SidebarFooter = styled.div`
  padding: 0 1rem 1rem;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

// --- Collapsed rail (desktop-only) ---

export const CollapsedRailContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  height: 100%;
  padding: 0.75rem 0;
  @supports (height: 100dvh) {
    height: 100dvh;
    max-height: 100dvh;
  }
  ${contentTransitionStyles}
`;

export const CollapsedLogoButton = styled(IconButton)`
  position: relative;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  overflow: hidden;

  &:hover .logo-image {
    opacity: 0;
  }

  &:hover .expand-icon {
    opacity: 1;
  }
`;

export const CollapsedLogoImage = styled.img`
  width: 100%;
  height: 100%;
  transition: opacity 200ms;
`;

export const CollapsedExpandIconOverlay = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 200ms;
  color: ${(p) => p.theme.icon.colors.primary};
`;

export const CollapsedNavList = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

export const CollapsedNavItemWrapper = styled.div<{ $active?: boolean }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background-color: ${(p) =>
    p.$active ? p.theme.colors.background5 : "transparent"};

  &:hover {
    background-color: ${(p) => p.theme.colors.background5};
  }
`;

export const CollapsedNavLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: ${(p) => p.theme.text.colors.brand};
  text-decoration: none;
`;

export const CollapsedNavComingSoonItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  color: ${(p) => p.theme.text.colors.brand};
  opacity: 0.5;
  cursor: default;
`;

export const CollapsedSpacer = styled.div`
  flex: 1;
`;

export const CollapsedFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CollapsedNewChatButton = styled(IconButton)`
  width: 2.5rem;
  height: 2.5rem;
  background-color: ${(p) => p.theme.button.default.colors.background};
  color: ${(p) => p.theme.button.default.colors.text};
  border: solid 1px ${(p) => p.theme.button.default.colors.border};

  &:hover {
    background-color: ${(p) => p.theme.button.default.hover.background};
    color: ${(p) => p.theme.button.default.hover.text};
    border: solid 1px ${(p) => p.theme.button.default.hover.border};
  }

  &.Mui-disabled {
    background-color: ${(p) => p.theme.button.default.colors.background};
    color: ${(p) => p.theme.button.default.colors.text};
    border: solid 1px ${(p) => p.theme.button.default.colors.border};
    opacity: 0.5;
  }
`;
